import type {SubmissionStatus} from '../types';

const STABLE_BUSINESS_MESSAGES: Record<string, string> = {
    SIGNAL_EXPIRED: '信号已过期，不能继续下单',
    INVALID_PRICE_RELATION: '交易计划价格关系无效',
    INVALID_TRADE_DIRECTION: '交易方向与信号意图不一致',
    SIGNAL_DIRECTION_MISMATCH: '信号方向已发生变化，请刷新后重试',
    INVALID_SIGNAL_DIRECTION: '信号方向数据异常，请刷新后重试',
    SIGNAL_CONTEXT_MISMATCH: '信号上下文不一致，请刷新后重试',
    SIGNAL_CONTEXT_UNAVAILABLE: '信号上下文暂时不可用',
    SIGNAL_MISMATCH: 'AI 分析与当前信号不匹配',
    SIGNAL_NOT_FOUND: '关联信号不存在或已失效',
    AI_ANALYSIS_NOT_FOUND: 'AI 解读不存在或已失效',
    INVALID_INSTRUMENT_SPEC: '合约规格不可用，暂时不能下单',
    ORDER_SIZE_BELOW_MINIMUM: '预计数量低于合约最小下单量',
    MARK_PRICE_UNAVAILABLE: '实时标记价不可用，暂时不能下单',
    KLINE_DATA_UNAVAILABLE: '最新 K 线不可用，暂时不能下单',
    IDEMPOTENCY_KEY_CONFLICT: '订单参数已变化，请返回编辑后重新确认',
    INVALID_IDEMPOTENCY_KEY: '订单提交标识无效，请返回编辑后重新确认',
    INVALID_AI_SIGNAL: 'AI 信号订单信息不完整，请刷新后重新确认',
    INVALID_API_KEY: '请选择有效的 API Key',
    API_KEY_FORBIDDEN: '当前账户无权使用该 API Key',
    UNAUTHENTICATED: '登录状态已失效，请重新登录',
    QUICK_TRADE_DISABLED: '快捷交易功能未开启',
    EXPIRY_CHECK_FAILED: '信号有效性校验失败，请稍后重试',
    EXECUTION_FAILED: '交易所拒绝了订单请求',
    ORDER_FAILED: '该订单此前已经处理失败',
    RESERVATION_FAILED: '订单幂等占位失败，请稍后重试',
};

export function stableBusinessMessage(code: string | undefined, fallback?: string): string {
    return (code && STABLE_BUSINESS_MESSAGES[code]) ?? fallback ?? '下单失败';
}

export function formatSuccessfulOrderStatus(data: {
    cexOrderId?: string | null;
    systemOrderId?: number | null;
    riskControlOrderId?: number | null;
    message?: string | null;
}): string {
    if (data.cexOrderId) return `订单已成功提交 · 交易所订单ID: ${data.cexOrderId}`;
    if (data.systemOrderId) return `订单已成功提交 · 本地订单ID: ${data.systemOrderId}`;
    if (data.riskControlOrderId) return `订单已完成审核 · 审核单ID: ${data.riskControlOrderId}`;
    return data.message || '订单已成功提交';
}

export interface SubmissionErrorClassification {
    status: Extract<SubmissionStatus, 'processing' | 'failed' | 'unknown'>;
    code?: string;
    message: string;
}

/**
 * 只有无法确认服务端是否受理的传输异常进入 unknown。
 * 明确的 4xx/稳定业务码必须回到 failed，不能锁在结果查询状态。
 */
export function classifySubmissionError(error: any): SubmissionErrorClassification {
    const code = error?.response?.data?.code ?? error?.code;
    const statusCode = Number(error?.response?.status);
    const serverMessage = error?.response?.data?.message;

    if ('ORDER_PROCESSING' === code) {
        return {status: 'processing', code, message: '订单正在处理中，请勿重复提交'};
    }
    if ('ORDER_RESULT_UNKNOWN' === code) {
        return {status: 'unknown', code, message: '订单结果暂时未知，正在使用原幂等键查询'};
    }

    if ((code && code in STABLE_BUSINESS_MESSAGES)
        || (Number.isFinite(statusCode) && 400 <= statusCode && statusCode < 500)) {
        return {status: 'failed', code, message: stableBusinessMessage(code, serverMessage)};
    }

    return {
        status: 'unknown',
        code,
        message: '订单结果暂时未知，正在使用原幂等键查询',
    };
}
